'use client';

import { useState, useEffect } from 'react';
import MenuHero from './MenuHero';
import CategoryTabs from './CategoryTabs';
import PizzaCard from './PizzaCard';
import SeasonalSpecials from './SeasonalSpecials';
import IngredientShowcase from './IngredientShowcase';
import QuoteModal from './QuoteModal';
import Icon from '@/components/ui/AppIcon';


interface Ingredient {
  name: string;
  origin: string;
  description: string;
}

interface Pizza {
  id: number;
  name: string;
  description: string;
  price: string;
  image: string;
  alt: string;
  category: string;
  dietary: string[];
  ingredients: Ingredient[];
  story: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
}

interface SeasonalPizza {
  id: number;
  name: string;
  description: string;
  price: string;
  image: string;
  alt: string;
  availableUntil: string;
  badge: string;
}

interface ShowcaseIngredient {
  name: string;
  origin: string;
  description: string;
  image: string;
  alt: string;
  certification: string;
}

const MenuInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedPizzaName, setSelectedPizzaName] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  { id: 'all', name: 'Alle Pizzen', icon: 'Squares2X2Icon', description: 'Unser komplettes Menü ansehen' },
  { id: 'classic', name: 'Klassisch', icon: 'StarIcon', description: 'Traditionelle neapolitanische Favoriten' },
  { id: 'signature', name: 'Signatur', icon: 'SparklesIcon', description: 'Unsere einzigartigen Kreationen' },
  { id: 'white', name: 'Weiße Pizzen', icon: 'MoonIcon', description: 'Ohne Tomatensauce' },
  { id: 'specialty', name: 'Spezialität', icon: 'FireIcon', description: 'Premium-Auswahl' }];


  const pizzaMenu: Pizza[] = [
  {
    id: 1,
    name: 'Margherita DOC',
    description: 'Die Königin der Pizzen - San-Marzano-Tomaten, frischer Büffelmozzarella, Basilikum und natives Olivenöl extra auf unserer charakteristischen holzbefeuerten Kruste.',
    price: '',
    image: "https://images.unsplash.com/photo-1677357861085-2e5b33fdd8f9",
    alt: 'Klassische Margherita-Pizza mit frischen Basilikumblättern, geschmolzenem Mozzarella und roter Tomatensauce auf holzbefeuerter Kruste',
    category: 'classic',
    dietary: ['vegetarian'],
    ingredients: [
    { name: 'San-Marzano-Tomaten', origin: 'Kampanien, Italien', description: 'DOP-zertifizierte Tomaten, die in vulkanischem Boden angebaut werden, bei perfekter Reife von Hand gepflückt für süßen, säurearmen Geschmack' },
    { name: 'Büffelmozzarella', origin: 'Caserta, Italien', description: 'Frischer Büffelmilch-Mozzarella, täglich hergestellt, cremige Textur mit zarten würzigen Noten' },
    { name: 'Frisches Basilikum', origin: 'Lokale Bio-Farmen', description: 'Aromatische süße Basilikumblätter, nach dem Backen hinzugefügt, um ätherische Öle und lebendige Farbe zu bewahren' }],

    story: '1889 zu Ehren von Königin Margherita von Savoyen kreiert, repräsentiert diese Pizza die italienische Flagge mit ihren roten Tomaten, weißem Mozzarella und grünem Basilikum. Unsere Version bleibt dem originalen neapolitanischen Rezept treu, zertifiziert von der Associazione Verace Pizza Napoletana.'
  },
  {
    id: 2,
    name: 'Diavola Inferno',
    description: 'Für Schärfeliebhaber - scharfe Salami, frische Chilischoten, Mozzarella, Tomatensauce und ein Schuss mit Chili angereichertes Olivenöl.',
    price: '',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107b3b30f-1765319154480.png",
    alt: 'Scharfe Diavola-Pizza belegt mit Peperonischeiben, roten Chilischoten, geschmolzenem Käse auf Tomatenbasis',
    category: 'classic',
    dietary: ['spicy'],
    ingredients: [
    { name: 'Kalabrische Salami', origin: 'Kalabrien, Italien', description: 'Würzige gepökelte Schweinefleisch-Salami mit Paprika und Chili, für intensiven Geschmack gereift' },
    { name: 'Frische Chilischoten', origin: 'Süditalien', description: 'Handverlesene scharfe Paprika für authentische italienische Schärfe' },
    { name: 'Chiliöl', origin: 'Hausgemacht', description: 'Natives Olivenöl extra, 30 Tage lang mit getrockneten Chilischoten infundiert' }],

    story: 'Benannt nach dem italienischen Wort für Teufel, wärmt diese feurige Kreation seit Generationen Herzen und Gaumen in Neapel. Die perfekte Balance aus Schärfe und Geschmack macht sie zu einem Favoriten bei Würz-Enthusiasten.'
  },
  {
    id: 3,
    name: 'Quattro Formaggi',
    description: 'Ein Käseliebhaber-Traum - Mozzarella, Gorgonzola, Parmigiano Reggiano und Fontina-Käse mit einem Hauch frischen Thymians.',
    price: '',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14bd2b5b2-1764696426492.png",
    alt: 'Vier-Käse-Pizza mit geschmolzenem Mozzarella, Blauschimmelkäse, Parmesan und Fontina auf weißer Basis',
    category: 'white',
    dietary: ['vegetarian'],
    ingredients: [
    { name: 'Gorgonzola DOP', origin: 'Lombardei, Italien', description: 'Cremiger Blauschimmelkäse mit charakteristischer Äderung, für komplexen Geschmack gereift' },
    { name: 'Parmigiano Reggiano', origin: 'Emilia-Romagna, Italien', description: '24 Monate gereifter Hartkäse, nussige und kristalline Textur' },
    { name: 'Fontina Valle d\'Aosta', origin: 'Aostatal, Italien', description: 'Halbharter Bergkäse mit butterigem, leicht nussigem Geschmack' }],

    story: 'Diese weiße Pizza feiert die Vielfalt der italienischen Käseherstellungstraditionen. Jeder Käse wird sorgfältig aus verschiedenen Regionen ausgewählt und schafft eine harmonische Mischung, die Jahrhunderte handwerklicher Expertise zeigt.'
  },
  {
    id: 4,
    name: 'Prosciutto e Rucola',
    description: 'Elegante Einfachheit - Mozzarella, Tomatensauce, belegt mit dünn geschnittenem Prosciutto di Parma, frischer Rucola und gehobeltem Parmesan.',
    price: '',
    image: "https://images.unsplash.com/photo-1727198826805-e2db1bc7b47b",
    alt: 'Gourmet-Pizza mit dünnen Prosciutto-Scheiben, frischen grünen Rucola-Blättern und Parmesan-Spänen auf Tomatenbasis',
    category: 'signature',
    dietary: [],
    ingredients: [
    { name: 'Prosciutto di Parma', origin: 'Parma, Italien', description: 'DOP-zertifizierter luftgetrockneter Schinken, mindestens 12 Monate für zarte Süße gereift' },
    { name: 'Wilde Rucola', origin: 'Lokale Bio-Farmen', description: 'Pfeffrige Salatblätter, frisch nach dem Backen hinzugefügt für knackige Textur' },
    { name: 'Parmigiano-Reggiano-Späne', origin: 'Emilia-Romagna, Italien', description: 'Frisch gehobelter gereifter Parmesan für nussigen Umami-Abschluss' }],

    story: 'Ein moderner Klassiker, der den Reichtum von gepökeltem Schinken mit der Frische von Rucola verbindet. Diese Pizza repräsentiert die Evolution der neapolitanischen Tradition und umarmt hochwertige Zutaten, während sie zeitlose Techniken respektiert.'
  },
  {
    id: 5,
    name: 'Funghi Porcini',
    description: 'Erdig und aromatisch - wilde Steinpilze, Mozzarella, Knoblauch, Petersilie und Trüffelöl auf weißer Basis.',
    price: '',
    image: "https://images.unsplash.com/photo-1691854991883-75d9a24fcacf",
    alt: 'Pilz-Pizza mit geschnittenen Steinpilzen, geschmolzenem Käse, Kräutern auf weißer Crème-Basis',
    category: 'specialty',
    dietary: ['vegetarian'],
    ingredients: [
    { name: 'Steinpilze', origin: 'Toskanische Wälder, Italien', description: 'Wild geerntete Herbstpilze mit intensivem erdigem Aroma und fleischiger Textur' },
    { name: 'Schwarzes Trüffelöl', origin: 'Umbrien, Italien', description: 'Premium-Öl, mit schwarzen Trüffeln für luxuriösen Waldgeschmack infundiert' },
    { name: 'Frische Petersilie', origin: 'Lokale Bio-Farmen', description: 'Italienische glatte Petersilie für helle, krautige Noten' }],

    story: 'Inspiriert von den Herbstwäldern der Toskana, feiert diese Pizza den begehrten Steinpilz. Die Zugabe von Trüffelöl hebt sie zu einem wahrhaft luxuriösen Erlebnis, perfekt für Pilz-Enthusiasten.'
  },
  {
    id: 6,
    name: 'Marinara Tradizionale',
    description: 'Die Wahl der Puristen - San-Marzano-Tomaten, Knoblauch, Oregano und natives Olivenöl extra. Kein Käse, purer Geschmack.',
    price: '',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1769c9345-1765214342986.png",
    alt: 'Traditionelle Marinara-Pizza mit roter Tomatensauce, Knoblauch, Oregano und Olivenöl ohne Käse',
    category: 'classic',
    dietary: ['vegan'],
    ingredients: [
    { name: 'San-Marzano-Tomaten', origin: 'Kampanien, Italien', description: 'DOP-zertifizierte Tomaten, der Goldstandard für Pizzasauce' },
    { name: 'Frischer Knoblauch', origin: 'Sizilien, Italien', description: 'Dünn geschnittene Knoblauchzehen für aromatischen Punch' },
    { name: 'Wilder Oregano', origin: 'Süditalien', description: 'Sonnengetrockneter Berg-Oregano mit intensivem mediterranem Geschmack' }],

    story: 'Die älteste Pizza in der neapolitanischen Tradition, stammt aus den 1700er Jahren. Benannt nach den Seeleuten (marinai), die diese einfache, aber geschmackvolle Pizza genossen. Es ist der ultimative Test für die Fähigkeiten eines Pizzabäckers - nirgendwo, wo man sich hinter Belägen verstecken kann.'
  },
  {
    id: 7,
    name: 'Capricciosa',
    description: 'Ein Festmahl auf einer Pizza - Mozzarella, Tomatensauce, Schinken, Artischocken, Pilze, Oliven und ein Hauch Oregano.',
    price: '',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12773ca67-1765630893941.png",
    alt: 'Beladene Capricciosa-Pizza mit Schinken, Artischocken, Pilzen, schwarzen Oliven auf Tomaten- und Käsebasis',
    category: 'classic',
    dietary: [],
    ingredients: [
    { name: 'Artischockenherzen', origin: 'Latium, Italien', description: 'Zarte römische Artischocken, in Olivenöl konserviert' },
    { name: 'Champignon-Pilze', origin: 'Lokale Farmen', description: 'Frische Champignons, geschnitten und sautiert' },
    { name: 'Taggiasca-Oliven', origin: 'Ligurien, Italien', description: 'Kleine, aromatische schwarze Oliven mit fruchtigen Noten' }],

    story: 'Der Name bedeutet "launisch" oder "eigensinnig", was die großzügige Vielfalt an Belägen widerspiegelt. Diese Pizza repräsentiert italienische Fülle und die Freude, ein mit verschiedenen Aromen und Texturen beladenes Essen zu teilen.'
  },
  {
    id: 8,
    name: 'Burrata e Pomodorini',
    description: 'Luxuriös und frisch - cremiger Burrata-Käse, Kirschtomaten, frisches Basilikum und Balsamico-Glasur auf weißer Basis.',
    price: '',
    image: "https://images.unsplash.com/photo-1590015115602-37b0f6227b1b",
    alt: 'Gourmet-Pizza mit cremigem Burrata-Käse in der Mitte, halbierten Kirschtomaten, Basilikumblättern und Balsamico-Träufeln',
    category: 'signature',
    dietary: ['vegetarian'],
    ingredients: [
    { name: 'Burrata-Käse', origin: 'Apulien, Italien', description: 'Frische Mozzarella-Hülle gefüllt mit cremiger Stracciatella und Sahne' },
    { name: 'Kirschtomaten', origin: 'Sizilien, Italien', description: 'Süße, sonnengereifte Kirschtomaten, geröstet, um den Geschmack zu konzentrieren' },
    { name: 'Gereifter Balsamico', origin: 'Modena, Italien', description: '12 Jahre gereifter Balsamico-Essig, dick und süß' }],

    story: 'Eine moderne Interpretation, die den unglaublichen Burrata-Käse aus Apulien präsentiert. Der Kontrast zwischen dem cremigen Käse und den süßen Tomaten schafft ein ausgeklügeltes Geschmacksprofil, das dies zu einem zeitgenössischen Favoriten gemacht hat.'
  },
  {
    id: 9,
    name: 'Salsiccia e Friarielli',
    description: 'Neapolitanisches Soul Food - italienische Wurst, Friarielli (Brokkoli-Rabe), Mozzarella und ein Hauch Chili auf weißer Basis.',
    price: '',
    image: "https://images.unsplash.com/photo-1717960952140-439ed54fdecd",
    alt: 'Rustikale Pizza mit italienischen Wurststücken, dunkelgrünem Brokkoli-Rabe, geschmolzenem Käse auf weißer Basis',
    category: 'specialty',
    dietary: ['spicy'],
    ingredients: [
    { name: 'Neapolitanische Wurst', origin: 'Kampanien, Italien', description: 'Grob gemahlene Schweinefleischwurst mit Fenchelsamen und schwarzem Pfeffer' },
    { name: 'Friarielli', origin: 'Neapel-Region, Italien', description: 'Bitterer Brokkoli-Rabe mit Knoblauch und Chili sautiert' },
    { name: 'Geräucherter Provola', origin: 'Kampanien, Italien', description: 'Geräucherter Pasta-Filata-Käse für Geschmackstiefe' }],

    story: 'Ein echter Geschmack der neapolitanischen Straßenesskultur. Diese Kombination aus Wurst und bitteren Grüns wird von Einheimischen geliebt und repräsentiert die mutigen, kompromisslosen Aromen der neapolitanischen Küche.'
  },
  {
    id: 10,
    name: 'Vegetariana Suprema',
    description: 'Gartenfrisch - gegrillte Zucchini, Aubergine, Paprika, rote Zwiebeln, Kirschtomaten und Mozzarella.',
    price: '',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dde0b131-1767366179529.png",
    alt: 'Bunte vegetarische Pizza mit gegrillter Zucchini, Aubergine, roten und gelben Paprika, Zwiebeln, Tomaten auf Käsebasis',
    category: 'signature',
    dietary: ['vegetarian'],
    ingredients: [
    { name: 'Gegrilltes Gemüse', origin: 'Lokale Bio-Farmen', description: 'Saisonales Gemüse gegrillt, um natürliche Süße zu verstärken' },
    { name: 'Geröstete Paprika', origin: 'Süditalien', description: 'Rote und gelbe Paprika geröstet, bis sie zart und süß sind' },
    { name: 'Karamellisierte Zwiebeln', origin: 'Lokale Farmen', description: 'Rote Zwiebeln langsam gekocht, bis sie süß und marmeladig sind' }],

    story: 'Die Fülle italienischer Gärten feiernd, beweist diese Pizza, dass vegetarische Optionen genauso befriedigend und aromatisch sein können. Jedes Gemüse wird zubereitet, um seine natürlichen Eigenschaften hervorzuheben.'
  },
  {
    id: 11,
    name: 'Nduja Piccante',
    description: 'Kalabrisches Feuer - scharfer Nduja-Aufstrich, Mozzarella, rote Zwiebeln und Honigträufeln für süß-scharf Balance.',
    price: '',
    image: "https://images.unsplash.com/photo-1672246934563-f07b57306f54",
    alt: 'Scharfe Pizza mit rotem Nduja-Aufstrich, geschmolzenem Käse, geschnittenen roten Zwiebeln und Honigträufeln',
    category: 'specialty',
    dietary: ['spicy'],
    ingredients: [
    { name: 'Nduja Calabrese', origin: 'Kalabrien, Italien', description: 'Scharfe, streichfähige Schweinefleisch-Salami mit intensiver Chilischärfe' },
    { name: 'Italienischer Honig', origin: 'Toskanische Hügel, Italien', description: 'Wildblumen-Honig für süßen Kontrast zu scharfem Nduja' },
    { name: 'Rote Zwiebeln', origin: 'Tropea, Kalabrien', description: 'Süße rote Zwiebeln berühmt für ihren milden, delikaten Geschmack' }],

    story: 'Diese Pizza präsentiert Nduja, die feurige streichfähige Salami aus Kalabrien. Die Zugabe von Honig schafft eine süchtig machende süß-scharfe Kombination, die zu einem modernen Klassiker geworden ist.'
  },
  {
    id: 12,
    name: 'Vegan Mediterranea',
    description: 'Pflanzenbasierte Perfektion - veganer Mozzarella, sonnengetrocknete Tomaten, Artischocken, Oliven, Kapern und frischer Oregano.',
    price: '',
    image: "https://images.unsplash.com/photo-1636727096401-4f846ede8f4b",
    alt: 'Vegane Pizza mit pflanzenbasiertem Käse, sonnengetrockneten Tomaten, Artischockenherzen, schwarzen Oliven, Kapern auf Tomatenbasis',
    category: 'signature',
    dietary: ['vegan', 'vegetarian'],
    ingredients: [
    { name: 'Cashew-Mozzarella', origin: 'Hausgemacht', description: 'Cremiger pflanzenbasierter Käse aus Cashewnüssen und Probiotika' },
    { name: 'Sonnengetrocknete Tomaten', origin: 'Sizilien, Italien', description: 'Tomaten in mediterraner Sonne getrocknet für konzentrierten Geschmack' },
    { name: 'Sizilianische Kapern', origin: 'Pantelleria, Italien', description: 'Salzkonservierte Kapern mit intensivem salzigem Geschmack' }],

    story: 'Der Beweis, dass vegane Pizza genauso köstlich und authentisch sein kann, verwendet diese Kreation traditionelle mediterrane Zutaten in einem pflanzenbasierten Format. Jedes Element wird sorgfältig für maximalen Geschmack ausgewählt.'
  }];


  const seasonalSpecials: SeasonalPizza[] = [
  {
    id: 101,
    name: 'Winter-Trüffel-Traum',
    description: 'Luxuriöses Winterspecial mit schwarzer Trüffelpaste, wilden Pilzen, Fontina-Käse und frischem Thymian auf weißer Basis.',
    price: '€22.00',
    image: "https://images.unsplash.com/photo-1599731316529-77e2c6b2734a",
    alt: 'Luxus-Pizza mit schwarzen Trüffelspänen, gemischten Pilzen, geschmolzenem Fontina-Käse und Thymianzweigen',
    availableUntil: '31. März 2025',
    badge: 'Winter-Special'
  },
  {
    id: 102,
    name: 'Festlicher Speck & Radicchio',
    description: 'Urlaubsinspirierte Kreation mit geräuchertem Speck, Radicchio, Gorgonzola, Walnüssen und Balsamico-Reduktion.',
    price: '€19.00',
    image: "https://images.unsplash.com/photo-1560384418-4339cfb525dd",
    alt: 'Gourmet-Pizza mit dünnen Speckscheiben, lila Radicchio-Blättern, Blauschimmelkäse, zerkleinerten Walnüssen und Balsamico-Glasur',
    availableUntil: '28. Februar 2025',
    badge: 'Feiertagsedition'
  },
  {
    id: 103,
    name: 'Butternut-Kürbis-Genuss',
    description: 'Herbstkomfort mit geröstetem Butternut-Kürbis, Salbei, Ricotta, knusprigem Pancetta und Kürbiskernöl.',
    price: '€17.50',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18acdd48f-1768224684067.png",
    alt: 'Saisonale Pizza mit gerösteten orangen Butternut-Kürbiswürfeln, Ricotta-Tupfen, knusprigem Pancetta und Salbeiblättern',
    availableUntil: '31. Januar 2025',
    badge: 'Herbst-Ernte'
  }];


  const showcaseIngredients: ShowcaseIngredient[] = [
  {
    name: 'San-Marzano-Tomaten',
    origin: 'Kampanien, Italien',
    description: 'DOP-zertifizierte Tomaten, die in vulkanischem Boden in der Nähe des Vesuvs angebaut werden. Bei perfekter Reife von Hand gepflückt für natürlich süßen, säurearmen Geschmack.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b616ac3-1766047146828.png",
    alt: 'Frische rote San-Marzano-Tomaten an der Rebe mit vulkanischem Boden im Hintergrund',
    certification: 'DOP-zertifiziert'
  },
  {
    name: 'Büffelmozzarella',
    origin: 'Caserta, Italien',
    description: 'Frischer Büffelmilch-Mozzarella, täglich nach traditionellen Methoden hergestellt. Cremige Textur mit zarten würzigen Noten und überlegenen Schmelzeigenschaften.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_190f70a2a-1766047153604.png",
    alt: 'Frische weiße Büffelmozzarella-Kugeln in Salzlake mit Basilikumblättern',
    certification: 'DOP-zertifiziert'
  },
  {
    name: 'Natives Olivenöl Extra',
    origin: 'Toskana, Italien',
    description: 'Kaltgepresst aus handgeernteten toskanischen Oliven. Fruchtiges Aroma mit pfeffrigem Abgang, perfekt zum Beträufeln und Kochen.',
    image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5',
    alt: 'Goldenes natives Olivenöl extra wird aus einer Glasflasche mit grünen Oliven gegossen',
    certification: 'IGP-zertifiziert'
  },
  {
    name: 'Tipo 00 Mehl',
    origin: 'Emilia-Romagna, Italien',
    description: 'Fein gemahlenes italienisches Mehl speziell für Pizzateig. Schafft die perfekte Balance aus knuspriger Außenseite und weichem, luftigem Inneren.',
    image: "https://images.unsplash.com/photo-1627735483792-233bf632619b",
    alt: 'Feines weißes italienisches 00-Mehl in Holzschüssel mit Weizenhalmen',
    certification: 'Bio-zertifiziert'
  }];


  const handleQuoteRequest = (pizzaId: number) => {
    if (!isHydrated) return;

    const pizza = pizzaMenu.find((p) => p.id === pizzaId);
    if (pizza) {
      setSelectedPizzaName(pizza.name);
      setIsQuoteModalOpen(true);
    }
  };

  const filteredPizzas = pizzaMenu.filter((pizza) => {
    const matchesCategory = activeCategory === 'all' || pizza.category === activeCategory;
    const matchesSearch = pizza.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pizza.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pizza.ingredients.some((ing) => ing.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilters = activeFilters.length === 0 ||
    activeFilters.every((filter) => pizza.dietary.includes(filter));

    return matchesCategory && matchesSearch && matchesFilters;
  });

  return (
    <div data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:364:4" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="364" data-component-file="MenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <MenuHero data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:365:6" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="365" data-component-file="MenuInteractive.tsx" data-component-name="MenuHero" data-component-content="%7B%22elementName%22%3A%22MenuHero%22%7D"
        onSearchChange={setSearchQuery}
        onFilterChange={setActiveFilters}
        activeFilters={activeFilters} />


      <CategoryTabs data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:371:6" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="371" data-component-file="MenuInteractive.tsx" data-component-name="CategoryTabs" data-component-content="%7B%22elementName%22%3A%22CategoryTabs%22%7D"
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory} />


      <section data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:377:6" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="377" data-component-file="MenuInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20px-6%22%7D" className="py-16 px-6">
        <div data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:378:8" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="378" data-component-file="MenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
          {filteredPizzas.length === 0 ?
          <div data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:380:10" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="380" data-component-file="MenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-20%22%7D" className="text-center py-20">
              <div data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:381:14" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="381" data-component-file="MenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-20%20h-20%20bg-muted%20rounded-full%20mb-6%22%7D" className="inline-flex items-center justify-center w-20 h-20 bg-muted rounded-full mb-6">
                <Icon data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:382:16" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="382" data-component-file="MenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="MagnifyingGlassIcon" size={40} className="text-muted-foreground" />
              </div>
              <h3 data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:384:14" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="384" data-component-file="MenuInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-bold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Keine%20Pizzen%20gefunden%22%7D" className="font-headline text-2xl font-bold text-foreground mb-2">
                Keine Pizzen gefunden
              </h3>
              <p data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:387:14" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="387" data-component-file="MenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-muted-foreground%22%2C%22textContent%22%3A%22Versuchen%20Sie%2C%20Ihre%20Suche%20oder%20Filter%20anzupassen%22%7D" className="font-body text-muted-foreground">
                Versuchen Sie, Ihre Suche oder Filter anzupassen
              </p>
            </div> :

          <>
              <div data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:393:14" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="393" data-component-file="MenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
                <p data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:394:16" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="394" data-component-file="MenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-muted-foreground%22%2C%22textContent%22%3A%22werden%20angezeigt%22%7D" className="font-body text-muted-foreground">
                  {filteredPizzas.length} {filteredPizzas.length === 1 ? 'Pizza' : 'Pizzen'} werden angezeigt
                </p>
              </div>
              <div data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:398:14" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="398" data-component-file="MenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPizzas.map((pizza) =>
              <PizzaCard data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:400:14" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="400" data-component-file="MenuInteractive.tsx" data-component-name="PizzaCard" data-component-content="%7B%22elementName%22%3A%22PizzaCard%22%7D"
                key={pizza.id}
                pizza={pizza}
                onQuoteRequest={handleQuoteRequest} />

              )}
              </div>
            </>
          }
        </div>
      </section>

      <SeasonalSpecials data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:412:6" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="412" data-component-file="MenuInteractive.tsx" data-component-name="SeasonalSpecials" data-component-content="%7B%22elementName%22%3A%22SeasonalSpecials%22%7D" specials={seasonalSpecials} />

      <IngredientShowcase data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:414:6" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="414" data-component-file="MenuInteractive.tsx" data-component-name="IngredientShowcase" data-component-content="%7B%22elementName%22%3A%22IngredientShowcase%22%7D" ingredients={showcaseIngredients} />

      <QuoteModal data-component-id="src/app/interactive-menu/components/MenuInteractive.tsx:416:6" data-component-path="src/app/interactive-menu/components/MenuInteractive.tsx" data-component-line="416" data-component-file="MenuInteractive.tsx" data-component-name="QuoteModal" data-component-content="%7B%22elementName%22%3A%22QuoteModal%22%7D"
        isOpen={isQuoteModalOpen}
        pizzaName={selectedPizzaName}
        onClose={() => setIsQuoteModalOpen(false)} />

    </div>);

};

export default MenuInteractive;